/**
 * LogService - prosty system logowania z fingerprint urządzenia
 *
 * Identyfikacja:
 * - fingerprint (fp) - unikalny hash urządzenia
 * - sessionId (sid) - unikalny per sesja przeglądarki (sessionStorage)
 *
 * Użycie:
 * LogService.init(__CFG.primaryEndpoint);
 * LogService.log('wiadomość', obiekt, 'kolejna wiadomość');
 */

var LogService = (function() {

  'use strict';

  var _fingerprint = null;
  var _sessionId = null;
  var _deviceId = null;
  var _endpoint = '';
  var _buffer = [];
  var _maxBufferSize = 50;
  var _flushInterval = null;
  var _failCount = 0;
  var _maxFails = 5;
  var _initialized = false;
  var _fingerprintReady = false;

  // ============================================
  // FINGERPRINT COLLECTOR
  // ============================================

  var FingerprintCollector = {

    collect: function(callback) {
      var self = this;
      var signals = {};
      var pending = 3;            // canvas + webgl + audio
      var completed = 0;

      var checkComplete = function() {
        completed++;
        if (completed >= pending) {
          self.finalize(signals, callback);
        }
      };

      // === Sygnały synchroniczne (STABILNE między sesjami) ===
      var s = window.screen || {};
      var n = navigator || {};

      signals.screen = [s.width, s.height, s.colorDepth, s.pixelDepth, window.devicePixelRatio].join('x');
      signals.navigator = [
        n.userAgent, n.language, (n.languages || []).join(','), n.platform,
        n.hardwareConcurrency, n.deviceMemory, n.maxTouchPoints, n.vendor, n.productSub
      ].join('|');
      signals.timezone = (function() {
        try { return Intl.DateTimeFormat().resolvedOptions().timeZone; }
        catch(e) { return new Date().getTimezoneOffset(); }
      })();
      signals.tzoffset = new Date().getTimezoneOffset();
      signals.plugins = (function() {
        var p = n.plugins || [];
        var names = [];
        for (var i = 0; i < Math.min(p.length, 8); i++) {
          if (p[i] && p[i].name) names.push(p[i].name);
        }
        return names.join('|');
      })();
      signals.fonts = self.detectFonts();
      signals.features = self.collectFeatures();

      // === Sygnały asynchroniczne (wymagają render/audio) ===
      this.getCanvasFingerprint(function(result) {
        signals.canvas = result;
        checkComplete();
      });

      this.getWebGLInfo(function(result) {
        signals.webgl = result;
        checkComplete();
      });

      this.getAudioFingerprint(function(result) {
        signals.audio = result;
        checkComplete();
      });
    },

    getCanvasFingerprint: function(callback) {
      try {
        var canvas = document.createElement('canvas');
        canvas.width = 280;
        canvas.height = 60;
        var ctx = canvas.getContext('2d');

        if (!ctx) {
          callback('no-canvas');
          return;
        }

        // Bogatszy render = więcej entropii silnika renderującego/GPU:
        // font fallback, emoji, przezroczystość, tryb mieszania, łuki.
        ctx.textBaseline = 'alphabetic';
        ctx.fillStyle = '#f60';
        ctx.fillRect(125, 1, 62, 20);
        ctx.fillStyle = '#069';
        ctx.font = '11pt no-real-font-123';
        ctx.fillText('Cwm fjordbank 😃 1234567890', 2, 15);
        ctx.fillStyle = 'rgba(102, 204, 0, 0.7)';
        ctx.font = '18pt Arial';
        ctx.fillText('Cwm fjordbank 😃', 4, 45);

        ctx.globalCompositeOperation = 'multiply';
        ctx.fillStyle = 'rgb(255,0,255)';
        ctx.beginPath(); ctx.arc(50, 50, 50, 0, Math.PI * 2, true); ctx.closePath(); ctx.fill();
        ctx.fillStyle = 'rgb(0,255,255)';
        ctx.beginPath(); ctx.arc(100, 50, 50, 0, Math.PI * 2, true); ctx.closePath(); ctx.fill();

        callback(this.simpleHash(canvas.toDataURL()));
      } catch (e) {
        callback('canvas-error');
      }
    },

    getWebGLInfo: function(callback) {
      try {
        var canvas = document.createElement('canvas');
        var gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');

        if (!gl) {
          callback('no-webgl');
          return;
        }

        var parts = [];
        var debugInfo = gl.getExtension('WEBGL_debug_renderer_info');
        parts.push(gl.getParameter(gl.VENDOR));
        parts.push(gl.getParameter(gl.RENDERER));
        if (debugInfo) {
          parts.push(gl.getParameter(debugInfo.UNMASKED_VENDOR_WEBGL));
          parts.push(gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL));
        }
        parts.push(gl.getParameter(gl.VERSION));
        parts.push(gl.getParameter(gl.SHADING_LANGUAGE_VERSION));

        // Parametry sprzętowe (stabilne per GPU/sterownik) = dużo entropii
        var pnames = ['MAX_TEXTURE_SIZE', 'MAX_VIEWPORT_DIMS', 'MAX_VERTEX_ATTRIBS',
          'MAX_VERTEX_UNIFORM_VECTORS', 'MAX_FRAGMENT_UNIFORM_VECTORS',
          'MAX_CUBE_MAP_TEXTURE_SIZE', 'MAX_RENDERBUFFER_SIZE',
          'ALIASED_LINE_WIDTH_RANGE', 'ALIASED_POINT_SIZE_RANGE', 'MAX_TEXTURE_IMAGE_UNITS'];
        for (var i = 0; i < pnames.length; i++) {
          try { parts.push(pnames[i] + '=' + gl.getParameter(gl[pnames[i]])); } catch (e) {}
        }
        try {
          var ext = gl.getSupportedExtensions();
          if (ext) parts.push('ext:' + ext.join(','));
        } catch (e) {}

        callback(this.simpleHash(parts.join('|')));
      } catch (e) {
        callback('webgl-error');
      }
    },

    // Detekcja zainstalowanych fontów przez pomiar szerokości/wysokości tekstu
    // względem fontów bazowych. Lista wykrytych fontów = mocny, stabilny sygnał.
    detectFonts: function() {
      try {
        var body = document.body || document.getElementsByTagName('body')[0];
        if (!body) return 'no-body';

        var baseFonts = ['monospace', 'sans-serif', 'serif'];
        var testString = 'mmmmmmmmmmlli';
        var testSize = '72px';

        var span = document.createElement('span');
        span.style.position = 'absolute';
        span.style.left = '-9999px';
        span.style.fontSize = testSize;
        span.style.lineHeight = 'normal';
        span.innerHTML = testString;

        var defaultW = {}, defaultH = {};
        var i, j;
        for (i = 0; i < baseFonts.length; i++) {
          span.style.fontFamily = baseFonts[i];
          body.appendChild(span);
          defaultW[baseFonts[i]] = span.offsetWidth;
          defaultH[baseFonts[i]] = span.offsetHeight;
          body.removeChild(span);
        }

        var fontList = ['Arial', 'Arial Black', 'Arial Narrow', 'Calibri', 'Cambria',
          'Comic Sans MS', 'Consolas', 'Courier', 'Courier New', 'Georgia', 'Helvetica',
          'Impact', 'Lucida Console', 'Lucida Sans Unicode', 'Microsoft Sans Serif',
          'Palatino Linotype', 'Segoe UI', 'Segoe Print', 'Tahoma', 'Times', 'Times New Roman',
          'Trebuchet MS', 'Verdana', 'MS Gothic', 'MS PGothic', 'MS Mincho', 'Meiryo',
          'Yu Gothic', 'Malgun Gothic', 'SimSun', 'SimHei', 'Microsoft YaHei',
          'Cantarell', 'DejaVu Sans', 'Liberation Sans', 'Ubuntu', 'Roboto', 'Noto Sans',
          'San Francisco', 'Menlo', 'Monaco', 'Andale Mono', 'Apple Color Emoji'];

        var detected = [];
        for (i = 0; i < fontList.length; i++) {
          var matched = false;
          for (j = 0; j < baseFonts.length; j++) {
            span.style.fontFamily = "'" + fontList[i] + "'," + baseFonts[j];
            body.appendChild(span);
            var m = (span.offsetWidth !== defaultW[baseFonts[j]] || span.offsetHeight !== defaultH[baseFonts[j]]);
            body.removeChild(span);
            if (m) { matched = true; break; }
          }
          if (matched) detected.push(fontList[i]);
        }
        return detected.join(',');
      } catch (e) {
        return 'font-error';
      }
    },

    // Cechy/kodeki — stabilne sygnały sprzętowo-konfiguracyjne.
    // Świadomie pomijamy prefers-color-scheme/reduced-motion (zmienne dzień/noc).
    collectFeatures: function() {
      try {
        var n = navigator || {};
        var mm = function(q) { try { return (window.matchMedia && window.matchMedia(q).matches) ? 1 : 0; } catch (e) { return 0; } };
        var codec = function(t) { try { var v = document.createElement('video'); return v.canPlayType(t) || ''; } catch (e) { return ''; } };
        return [
          'ce=' + (n.cookieEnabled ? 1 : 0),
          'dnt=' + (n.doNotTrack || ''),
          'touch=' + (('ontouchstart' in window) ? 1 : 0),
          'gamut=' + (mm('(color-gamut: p3)') ? 'p3' : (mm('(color-gamut: srgb)') ? 'srgb' : '')),
          'h264=' + codec('video/mp4; codecs="avc1.42E01E"'),
          'webm=' + codec('video/webm; codecs="vp9"'),
          'ogg=' + codec('audio/ogg; codecs="vorbis"')
        ].join('|');
      } catch (e) {
        return 'feat-error';
      }
    },

    // Audio fingerprint — OfflineAudioContext renderuje sygnał, a różnice w
    // implementacji DSP (CPU/OS/przeglądarka) dają stabilny hash.
    getAudioFingerprint: function(callback) {
      var self = this;
      try {
        var Ctx = window.OfflineAudioContext || window.webkitOfflineAudioContext;
        if (!Ctx) { callback('no-audio'); return; }

        var ctx = new Ctx(1, 44100, 44100);
        var osc = ctx.createOscillator();
        osc.type = 'triangle';
        osc.frequency.value = 10000;

        var comp = ctx.createDynamicsCompressor();
        if (comp.threshold) comp.threshold.value = -50;
        if (comp.knee) comp.knee.value = 40;
        if (comp.ratio) comp.ratio.value = 12;
        if (comp.attack) comp.attack.value = 0;
        if (comp.release) comp.release.value = 0.25;

        osc.connect(comp);
        comp.connect(ctx.destination);
        osc.start(0);
        ctx.startRendering();

        var done = false;
        ctx.oncomplete = function(e) {
          if (done) return;
          done = true;
          try {
            var buf = e.renderedBuffer.getChannelData(0);
            var sum = 0;
            for (var i = 4500; i < 5000; i++) sum += Math.abs(buf[i]);
            callback(self.simpleHash(sum.toString()));
          } catch (err) {
            callback('audio-read-error');
          }
        };
        // fallback gdy rendering utknie
        setTimeout(function() { if (!done) { done = true; callback('audio-timeout'); } }, 1000);
      } catch (e) {
        callback('audio-error');
      }
    },

    // Finalny hash. Preferuje SHA-256 (SubtleCrypto, kontekst secure/https) —
    // odporny na kolizje; fallback do wewnętrznego hashSignals.
    finalize: function(signals, callback) {
      var self = this;
      try {
        if (window.crypto && window.crypto.subtle && window.isSecureContext && window.TextEncoder) {
          var enc = new TextEncoder().encode(JSON.stringify(signals));
          window.crypto.subtle.digest('SHA-256', enc).then(function(buf) {
            var arr = Array.prototype.slice.call(new Uint8Array(buf));
            var hex = arr.map(function(b) { return ('0' + b.toString(16)).slice(-2); }).join('');
            callback(hex.slice(0, 32)); // 128-bit prefix wystarcza jako stabilny id urządzenia
          }).catch(function() {
            callback(self.hashSignals(signals));
          });
          return;
        }
      } catch (e) {}
      callback(self.hashSignals(signals));
    },

    simpleHash: function(str) {
      var hash = 0;
      if (!str || str.length === 0) return '0';
      for (var i = 0; i < str.length; i++) {
        hash = ((hash << 5) - hash) + str.charCodeAt(i);
        hash = hash & hash;
      }
      return Math.abs(hash).toString(36);
    },

    hashSignals: function(signals) {
      var str = JSON.stringify(signals);
      var h1 = 0xdeadbeef;
      var h2 = 0x41c6ce57;

      for (var i = 0; i < str.length; i++) {
        var ch = str.charCodeAt(i);
        h1 = Math.imul(h1 ^ ch, 2654435761);
        h2 = Math.imul(h2 ^ ch, 1597334677);
      }

      h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507) ^ Math.imul(h2 ^ (h2 >>> 13), 3266489909);
      h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507) ^ Math.imul(h1 ^ (h1 >>> 13), 3266489909);

      return (h2 >>> 0).toString(16) + (h1 >>> 0).toString(16);
    }
  };

  // ============================================
  // GŁÓWNY SERWIS
  // ============================================

  return {

    init: function(endpoint) {
      if (_initialized) return this;

      var self = this;
      _endpoint = endpoint || '';

      // Session ID - nowy per sesja przeglądarki
      _sessionId = sessionStorage.getItem('log_sid');
      if (!_sessionId) {
        _sessionId = 'ses_' + Date.now().toString(36) + '_' + Math.random().toString(36).substr(2, 9);
        sessionStorage.setItem('log_sid', _sessionId);
      }

      // Device ID - trwały per przeglądarka/urządzenie (localStorage).
      // Fingerprint na mobile KOLIDUJE między urządzeniami tego samego modelu
      // (iOS celowo, Android przez zredukowane UA) — did daje wiarygodne
      // velocity per-urządzenie tam, gdzie fp zawodzi (jak "cookie" Nethone).
      try {
        _deviceId = localStorage.getItem('log_did');
        if (!_deviceId) {
          _deviceId = 'did_' + Date.now().toString(36) + '_' + Math.random().toString(36).substr(2, 12);
          localStorage.setItem('log_did', _deviceId);
        }
      } catch (e) { _deviceId = null; }

      // Fingerprint
      if (window.fingerprint) {
        _fingerprint = window.fingerprint;
        _fingerprintReady = true;
      } else {
        FingerprintCollector.collect(function(hash) {
          _fingerprint = hash;
          _fingerprintReady = true;
          window.fingerprint = hash;
        });
      }

      // Auto-flush co 5s
      _flushInterval = setInterval(function() { self.flush(); }, 5000);

      // Flush przed zamknięciem
      var flushBeacon = function() { self.flush(true); };
      document.addEventListener('visibilitychange', function() {
        if (document.visibilityState === 'hidden') flushBeacon();
      }, true);
      window.addEventListener('pagehide', flushBeacon, true);
      window.addEventListener('beforeunload', flushBeacon, true);

      _initialized = true;
      return this;
    },

    /**
     * Logowanie - przyjmuje dowolną liczbę argumentów (jak console.log)
     */
    log: function() {
      var args = Array.prototype.slice.call(arguments);
      var msg = [];

      for (var i = 0; i < args.length; i++) {
        var a = args[i];
        msg.push(typeof a === 'string' ? a : JSON.stringify(a));
      }

      var now = new Date();
      var ts = now.getTime();
      var time = now.toISOString().replace('T', ' ').substr(0, 19);

      var entry = {
        ts: ts,
        time: time,
        msg: msg.join(' >>> ')
      };

      // Kontekst w pierwszym logu bufora
      if (_buffer.length === 0) {
        entry.ctx = {
          fp: _fingerprint || window.fingerprint || 'pending',
          sid: _sessionId,
          ua: navigator.userAgent,
          url: window.location.href
        };
      }

      _buffer.push(entry);

      if (_buffer.length >= _maxBufferSize) {
        this.flush();
      }

      return this;
    },

    flush: function(useBeacon) {
      var self = this;

      if (_buffer.length === 0 || _failCount >= _maxFails) return this;
      if (!_fingerprintReady && !window.fingerprint) return this;

      var payload = {
        fp: _fingerprint || window.fingerprint || 'unknown',
        sid: _sessionId,
        logs: _buffer.slice()
      };
      if (_deviceId) payload.did = _deviceId;

      _buffer = [];

      var url = _endpoint + '/log';
      var body = JSON.stringify(payload);

      if (useBeacon) {
        if (!navigator.sendBeacon(url, body)) {
          _buffer = payload.logs.concat(_buffer).slice(0, _maxBufferSize);
        }
        return this;
      }

      fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: body,
        keepalive: true
      })
      .then(function(response) {
        if (!response.ok) throw new Error('HTTP ' + response.status);
        _failCount = 0;
      })
      .catch(function() {
        _failCount++;
        if (payload.logs.length < 100) {
          _buffer = payload.logs.concat(_buffer).slice(0, _maxBufferSize);
        }
      });

      return this;
    },

    getFingerprint: function() { return _fingerprint; },
    getSessionId: function() { return _sessionId; },
    getDeviceId: function() { return _deviceId; }
  };

})();

if (typeof module !== 'undefined' && module.exports) {
  module.exports = LogService;
}

export default LogService;



// WEBPACK FOOTER //
// ./src/logService.js